/* ===========================
   BREAKIN' ATOMS — theme.js
   =========================== */

'use strict';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

// ─── Header scroll behaviour ─────────────────────────────────────────────────

const header = document.getElementById('site-header');
if (header) {
  window.addEventListener('scroll', () => {
    header.classList.toggle('is-scrolled', window.scrollY > 20);
  }, { passive: true });
}

// ─── Mobile menu ─────────────────────────────────────────────────────────────

const menuToggle = document.querySelector('.menu-toggle');
const mobileNav  = document.getElementById('mobile-nav');

if (menuToggle && mobileNav) {
  menuToggle.addEventListener('click', () => {
    const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
    menuToggle.setAttribute('aria-expanded', String(!isOpen));
    mobileNav.hidden = isOpen;
    document.body.style.overflow = isOpen ? '' : 'hidden';
  });
}

// ─── Search panel ────────────────────────────────────────────────────────────

const searchToggle = document.querySelector('.search-toggle');
const searchPanel  = document.getElementById('search-panel');
const searchClose  = document.querySelector('.search-close');

function openSearch() {
  if (!searchPanel) return;

  searchPanel.hidden = false;
  searchPanel.querySelector('.search-input')?.focus();
}

function closeSearch() {
  if (!searchPanel) return;

  searchPanel.hidden = true;
}

if (searchToggle) searchToggle.addEventListener('click', openSearch);
if (searchClose)  searchClose.addEventListener('click', closeSearch);

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && searchPanel && !searchPanel.hidden) {
    closeSearch();
  }
});

// ─── Cart Drawer ─────────────────────────────────────────────────────────────

const cartOverlay = document.getElementById('cart-overlay');
const cartDrawer  = document.getElementById('cart-drawer');
const cartCountEls = document.querySelectorAll('[data-cart-count]');

function openCart() {
  cartDrawer?.classList.add('is-open');
  cartOverlay?.classList.add('is-open');
  cartDrawer?.removeAttribute('aria-hidden');
  cartOverlay?.removeAttribute('aria-hidden');
  document.body.style.overflow = 'hidden';
  renderCart();
}

function closeCart() {
  cartDrawer?.classList.remove('is-open');
  cartOverlay?.classList.remove('is-open');
  cartDrawer?.setAttribute('aria-hidden', 'true');
  cartOverlay?.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

if (cartOverlay) cartOverlay.addEventListener('click', closeCart);
document.querySelectorAll('[data-cart-toggle]').forEach(btn => btn.addEventListener('click', openCart));
document.querySelectorAll('[data-cart-close]').forEach(btn => btn.addEventListener('click', closeCart));
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && cartDrawer?.classList.contains('is-open')) closeCart();
});

// ─── Cart API ────────────────────────────────────────────────────────────────

async function fetchCart() {
  const res = await fetch('/cart.js');
  return res.json();
}

async function addToCart(variantId, quantity = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity }),
  });
  if (!res.ok) throw new Error('Failed to add to cart');
  return res.json();
}

// Posts the full product form so app-injected line item properties
// (e.g. Globo Product Options -> properties[...]) persist to cart + checkout.
async function addFormToCart(form) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: new FormData(form),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.description || 'Failed to add to cart');
  return data;
}

async function updateCartItem(key, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });
  return res.json();
}

function updateCartCount(count) {
  cartCountEls.forEach(el => {
    el.textContent = count > 0 ? count : '';
    el.setAttribute('data-cart-count', count);
  });
}

// ─── Render Cart Drawer ───────────────────────────────────────────────────────

async function renderCart() {
  const body   = document.getElementById('cart-drawer-body');
  const footer = document.getElementById('cart-drawer-footer');
  const subtotalEl = document.getElementById('cart-subtotal');
  if (!body) return;

  body.innerHTML = '<p class="cart-drawer__empty">Loading…</p>';

  try {
    const cart = await fetchCart();
    updateCartCount(cart.item_count);

    if (cart.item_count === 0) {
      body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      if (footer) footer.hidden = true;
      return;
    }

    if (footer) footer.hidden = false;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item__img" src="${item.image}" alt="${item.title}" loading="lazy" width="64" height="64">
        <div>
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          ${Object.entries(item.properties || {})
            .filter(([key, value]) => value && !key.startsWith('_'))
            .map(([key, value]) => `<p class="cart-item__property">${key}: ${value}</p>`)
            .join('')}
          <div class="cart-item__qty">
            <button class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
            <span class="cart-item__qty-val">${item.quantity}</span>
            <button class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove item">Remove</button>
        </div>
        <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
      </div>
    `).join('');

    // Quantity / remove handlers
    body.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty);
        const newQty = btn.dataset.action === 'increase' ? qty + 1 : qty - 1;
        await updateCartItem(key, newQty);
        renderCart();
      });
    });
    body.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        await updateCartItem(btn.dataset.key, 0);
        renderCart();
      });
    });

  } catch (err) {
    console.error('[Cart]', err);
    body.innerHTML = '<p class="cart-drawer__empty">Something went wrong. Please refresh.</p>';
  }
}

// ─── Checkout ─────────────────────────────────────────────────────────────────

const checkoutBtn = document.getElementById('checkout-btn');
if (checkoutBtn) {
  checkoutBtn.addEventListener('click', () => {
    window.location.href = '/checkout';
  });
}

// ─── Add-to-cart buttons (product grid cards) ─────────────────────────────────

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.add-to-cart-btn');
  if (!btn || btn.disabled) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const originalText = btn.textContent.trim();
  btn.disabled = true;
  btn.textContent = 'Adding…';

  try {
    await addToCart(variantId);
    btn.textContent = 'Added!';
    setTimeout(() => {
      btn.textContent = originalText;
      btn.disabled = false;
    }, 1500);
    openCart();
  } catch (err) {
    console.error('[Add to cart]', err);
    btn.textContent = 'Error — try again';
    btn.disabled = false;
  }
});

// ─── Atom Burst Animation ─────────────────────────────────────────────────────

(function() {
  const NS = 'http://www.w3.org/2000/svg';
  const palette = [
    'hsl(220,12%,8%)',
    'hsl(35,20%,60%)',
    'hsl(28,22%,76%)',
    'hsl(36,14%,91%)',
    'hsl(220,12%,30%)',
  ];

  // Create SVG overlay
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('id', 'atom-burst-canvas');
  svg.setAttribute('aria-hidden', 'true');
  document.body.appendChild(svg);

  let particles = [];
  let animId = null;

  function spawnBurst(x, y) {
    const count = 18;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const speed = 2.5 + Math.random() * 4;
      const radius = 4 + Math.random() * 5;
      const color = palette[Math.floor(Math.random() * palette.length)];

      // Outer glow circle
      const glow = document.createElementNS(NS, 'circle');
      // Core circle
      const core = document.createElementNS(NS, 'circle');
      [glow, core].forEach(el => svg.appendChild(el));

      particles.push({ x, y, angle, speed, radius, opacity: 1, color, glow, core });
    }

    // Expanding ring
    const ring = document.createElementNS(NS, 'circle');
    ring.setAttribute('cx', x);
    ring.setAttribute('cy', y);
    ring.setAttribute('r', '1');
    ring.setAttribute('fill', 'none');
    ring.setAttribute('stroke', 'hsl(220,12%,8%)');
    ring.setAttribute('stroke-width', '1.5');
    ring.setAttribute('opacity', '0.5');
    ring.style.animation = 'atom-ring 0.5s ease-out forwards';
    svg.appendChild(ring);
    setTimeout(() => ring.remove(), 500);

    if (!animId) tick();
  }

  function tick() {
    particles = particles.map(p => ({
      ...p,
      x: p.x + Math.cos(p.angle) * p.speed,
      y: p.y + Math.sin(p.angle) * p.speed,
      opacity: p.opacity - 0.035,
      radius: p.radius * 0.97,
    }));

    const alive = [];
    particles.forEach(p => {
      if (p.opacity <= 0) {
        p.glow.remove();
        p.core.remove();
      } else {
        p.glow.setAttribute('cx', p.x);
        p.glow.setAttribute('cy', p.y);
        p.glow.setAttribute('r', p.radius + 2);
        p.glow.setAttribute('fill', p.color);
        p.glow.setAttribute('opacity', p.opacity * 0.2);

        p.core.setAttribute('cx', p.x);
        p.core.setAttribute('cy', p.y);
        p.core.setAttribute('r', p.radius);
        p.core.setAttribute('fill', p.color);
        p.core.setAttribute('opacity', p.opacity);

        alive.push(p);
      }
    });

    particles = alive;
    animId = particles.length > 0 ? requestAnimationFrame(tick) : null;
  }

  // Trigger on any link / button click
  document.addEventListener('click', function(e) {
    const target = e.target.closest('a, button');
    if (!target) return;
    const rect = target.getBoundingClientRect();
    spawnBurst(rect.left + rect.width / 2, rect.top + rect.height / 2);
  });
})();

// ─── Product page: variant selection ──────────────────────────────────────────

const variantSelect = document.getElementById('variant-select');

if (variantSelect) {
  const variants = JSON.parse(variantSelect.dataset.productVariants || '[]');
  const optionInputs = document.querySelectorAll('.option-value__input');
  const priceEl = document.getElementById('product-price');
  const addToCartBtn = document.getElementById('add-to-cart-btn');

  function getSelectedOptions() {
    const selected = {};
    optionInputs.forEach(input => {
      if (input.checked) selected[input.name] = input.value;
    });
    return Object.values(selected);
  }

  function findVariant(selectedOptions) {
    return variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function updateVariant() {
    const selectedOptions = getSelectedOptions();
    const variant = findVariant(selectedOptions);

    if (!variant) return;

    variantSelect.value = variant.id;

    if (addToCartBtn) {
      addToCartBtn.dataset.variantId = variant.id;
      addToCartBtn.disabled = !variant.available;
      addToCartBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    if (priceEl) {
      priceEl.innerHTML = variant.compare_at_price > variant.price
        ? `<s class="product-card__compare-price">${formatMoney(variant.compare_at_price * 100)}</s> <span>${formatMoney(variant.price * 100)}</span>`
        : `<span>${formatMoney(variant.price * 100)}</span>`;
    }
  }

  optionInputs.forEach(input => input.addEventListener('change', updateVariant));

  // Product gallery thumb switching
  document.querySelectorAll('.product-gallery__thumb').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const mainImg = document.getElementById('product-main-image');
      if (mainImg) {
        mainImg.src = thumb.dataset.imageSrc;
        if (thumb.dataset.imageSrcset) mainImg.srcset = thumb.dataset.imageSrcset;
        mainImg.alt = thumb.dataset.imageAlt;
      }
      document.querySelectorAll('.product-gallery__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  // ─── Lightbox Gallery ────────────────────────────────────────────────────────

  const lightbox = document.getElementById('product-lightbox');
  const lightboxImg = document.getElementById('lightbox-img');
  const lightboxCounter = document.getElementById('lightbox-current');
  const lightboxThumbs = document.querySelectorAll('.lightbox__thumb');
  const galleryMain = document.getElementById('product-gallery-main');
  let currentIndex = 0;

  function openLightbox(index) {
    if (!lightbox || !lightboxImg) return;
    currentIndex = index || 0;
    updateLightboxImage(currentIndex);
    lightbox.classList.add('is-open');
    lightbox.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function updateLightboxImage(index) {
    if (!lightboxThumbs.length) return;
    const thumb = lightboxThumbs[index];
    if (!thumb) return;
    lightboxImg.src = thumb.dataset.lightboxSrc;
    lightboxImg.alt = thumb.dataset.lightboxAlt;
    if (lightboxCounter) lightboxCounter.textContent = index + 1;
    lightboxThumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }

  function nextLightbox() {
    currentIndex = (currentIndex + 1) % lightboxThumbs.length;
    updateLightboxImage(currentIndex);
  }

  function prevLightbox() {
    currentIndex = (currentIndex - 1 + lightboxThumbs.length) % lightboxThumbs.length;
    updateLightboxImage(currentIndex);
  }

  // Open on main image click
  if (galleryMain) {
    galleryMain.addEventListener('click', () => {
      const activeThumb = document.querySelector('.product-gallery__thumb.is-active');
      const index = activeThumb ? parseInt(activeThumb.dataset.imageIndex, 10) : 0;
      openLightbox(index);
    });
    galleryMain.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(0); }
    });
  }

  // Lightbox controls
  if (lightbox) {
    lightbox.querySelector('.lightbox__close')?.addEventListener('click', closeLightbox);
    lightbox.querySelector('.lightbox__backdrop')?.addEventListener('click', closeLightbox);
    lightbox.querySelector('.lightbox__nav--next')?.addEventListener('click', nextLightbox);
    lightbox.querySelector('.lightbox__nav--prev')?.addEventListener('click', prevLightbox);

    lightboxThumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.dataset.lightboxIndex, 10);
        currentIndex = idx;
        updateLightboxImage(idx);
      });
    });
  }

  // Keyboard navigation in lightbox
  document.addEventListener('keydown', (e) => {
    if (!lightbox || !lightbox.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextLightbox();
    if (e.key === 'ArrowLeft') prevLightbox();
  });
}

// ─── Product form: AJAX add to cart (keeps app line item properties) ──────────

const productForm = document.getElementById('product-form');
if (productForm) {
  productForm.addEventListener('submit', async (e) => {
    // If a product-options app (Globo) has injected its own fields, let it own
    // the submit: it runs required-field validation and adds its line item
    // properties / price add-ons through a native form post.
    const hasAppOptions = productForm.querySelector(
      '[name^="properties["], .globo-optionsapp, [class*="globo"], [id*="globo"]'
    );
    if (hasAppOptions) return;

    e.preventDefault();
    const btn = productForm.querySelector('.product-form__submit');
    if (!btn || btn.disabled) return;

    const original = btn.textContent.trim();
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      await addFormToCart(productForm);
      btn.textContent = 'Added';
      const cart = await fetchCart();
      updateCartCount(cart.item_count);
      if (typeof openCart === 'function') openCart();
    } catch (err) {
      console.error('[Add to cart]', err);
      btn.textContent = err.message || 'Error — try again';
    } finally {
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1600);
    }
  });
}

// ─── Collection scroll restoration ───────────────────────────────────────────

(function () {
  const KEY = 'ba:collection-scroll';

  const collectionGrid = document.querySelector('[data-collection-grid], .collection-page');
  const path = window.location.pathname;

  // Save position when leaving a collection page via a product link
  if (collectionGrid) {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      try {
        sessionStorage.setItem(KEY, JSON.stringify({ path, y: window.scrollY }));
      } catch (_) {}
    });

    // Restore when returning to the same collection
    try {
      const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (saved && saved.path === path) {
        window.requestAnimationFrame(() => window.scrollTo(0, saved.y));
        sessionStorage.removeItem(KEY);
      }
    } catch (_) {}
  }
})();

/* --- Predictive search (Shopify /search/suggest.json) --- */
(function () {
  const input =
    document.querySelector('[data-search-input]') ||
    document.querySelector('.search-input') ||
    document.querySelector('input[type="search"][name="q"]');
  const panel = document.getElementById('predictive-search');
  const results = document.getElementById('predictive-search-results');
  const status = document.getElementById('predictive-search-status');
  const seeAll = document.getElementById('predictive-search-see-all');
  if (!input || !panel || !results || !status || !seeAll) return;

  const MIN_CHARS = 2;
  let debounceTimer;
  let currentAbort;
  let activeIndex = -1;

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));

  function open() {
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }
  function close() {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
  }

  function items() {
    return Array.prototype.slice.call(results.querySelectorAll('.predictive-search__result'));
  }

  function setActive(next) {
    const list = items();
    if (!list.length) return;
    list.forEach((el) => el.classList.remove('is-active'));
    activeIndex = (next + list.length) % list.length;
    const el = list[activeIndex];
    el.classList.add('is-active');
    el.scrollIntoView({ block: 'nearest' });
  }

  function render(products, query) {
    activeIndex = -1;
    if (!products || !products.length) {
      results.innerHTML = '';
      status.textContent = 'No matches for "' + query + '".';
      seeAll.hidden = true;
      return;
    }
    status.textContent = '';
    results.innerHTML = products
      .slice(0, 6)
      .map(function (p) {
        const img = p.featured_image && p.featured_image.url
          ? '<img src="' + esc(p.featured_image.url) + '" alt="' +
            esc(p.featured_image.alt || p.title) + '" loading="lazy" width="48" height="60">'
          : '';
        const price = p.price ? '<span class="predictive-search__price">' + esc(p.price) + '</span>' : '';
        return (
          '<li role="option">' +
            '<a class="predictive-search__result" href="' + esc(p.url) + '">' +
              '<span class="predictive-search__thumb">' + img + '</span>' +
              '<span class="predictive-search__text">' +
                '<span class="predictive-search__title">' + esc(p.title) + '</span>' +
                price +
              '</span>' +
            '</a>' +
          '</li>'
        );
      })
      .join('');
    seeAll.href = '/search?q=' + encodeURIComponent(query);
    seeAll.hidden = false;
  }

  function search(query) {
    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();
    status.textContent = 'Searching…';
    open();

    const url =
      '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=6' +
      '&resources[options][unavailable_products]=last';

    fetch(url, { signal: currentAbort.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('Predictive search failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        const products =
          (data && data.resources && data.resources.results && data.resources.results.products) || [];
        render(products, query);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error(err);
        results.innerHTML = '';
        seeAll.hidden = true;
        status.textContent = 'Search unavailable. Press enter to search all results.';
      });
  }

  input.addEventListener('input', function () {
    const q = input.value.trim();
    clearTimeout(debounceTimer);
    if (q.length < MIN_CHARS) {
      close();
      return;
    }
    debounceTimer = setTimeout(function () { search(q); }, 180);
  });

  input.addEventListener('focus', function () {
    if (input.value.trim().length >= MIN_CHARS && results.children.length) open();
  });

  input.addEventListener('keydown', function (e) {
    if (panel.hidden) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
    else if (e.key === 'Enter' && activeIndex > -1) {
      e.preventDefault();
      items()[activeIndex].click();
    }
  });

  document.addEventListener('click', function (e) {
    if (!panel.contains(e.target) && e.target !== input) close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });
})();
/* ── Lookbook grid: tap-to-reveal on touch devices ── */
(function () {
  if (!window.matchMedia('(hover: none)').matches) return;

  document.addEventListener('click', function (e) {
    var card = e.target.closest('.coll-grid .product-card');
    if (!card) return;

    // If info isn't revealed yet, reveal it and stop navigation
    if (!card.classList.contains('is-revealed')) {
      e.preventDefault();
      document.querySelectorAll('.coll-grid .product-card.is-revealed')
        .forEach(function (other) {
          if (other !== card) other.classList.remove('is-revealed');
        });
      card.classList.add('is-revealed');
    }
    // If already revealed, let the click through — user goes to product
  }, true);
})();